import type { KeyboardEvent } from 'react'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Input, InputPassword } from '@/common/ui/Form'
import { Button } from '@/common/ui/Form/Button'
import { useResendVerificationModel, useUpdateMe } from '@/app/entities/me/model'
import { hasUserEmail, hasVerifiedEmail } from '@/common/entities/me/lib/profileCompleteness'
import { cn } from '@/common/lib/cn/cn'
import { Modal } from '@/common/ui/Modal'
import { MeChangePasswordForm } from './meChangePasswordForm'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export const MeForm = ({ className }: { className?: string }) => {
    const { form, me, canEditEmail, handleSubmit, onSubmit, meLoading, register, isPending, isError } = useUpdateMe()
    const { handleResendVerification, isPending: isResendPending } = useResendVerificationModel()
    const showPasswordSection = hasVerifiedEmail(me)
    const showResendVerification = hasUserEmail(me) && !hasVerifiedEmail(me)

    const formClasses = cn(
        {
            skelet: meLoading,
        },
        className
    )

    const handleResendClick = () => {
        if (!me?.email) return
        handleResendVerification(me.email)
    }

    const handleResendKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (event.key !== 'Enter' && event.key !== ' ') return
        event.preventDefault()
        handleResendClick()
    }

    return (
        <>
            <form className={formClasses} onSubmit={handleSubmit(onSubmit)}>
                <div className='grid-col-1'>
                    <Fieldset label='Имя' required errorText={form.formState.errors.first_name?.message}>
                        <Input
                            className='w-full'
                            size='lg'
                            autoComplete='off'
                            placeholder='Введите имя'
                            {...register('first_name', {
                                required: 'Имя обязательно',
                            })}
                        />
                    </Fieldset>
                </div>
                <div className='grid-col-1'>
                    <Fieldset label='Фамилия' required errorText={form.formState.errors.last_name?.message}>
                        <Input
                            className='w-full'
                            size='lg'
                            autoComplete='off'
                            placeholder='Введите фамилию'
                            {...register('last_name', {
                                required: 'Фамилия обязательна',
                            })}
                        />
                    </Fieldset>
                </div>
                <div className='grid-col-1'>
                    <Fieldset label='Почта' required={canEditEmail} errorText={form.formState.errors.email?.message}>
                        {canEditEmail ? (
                            <Input
                                className='w-full'
                                size='lg'
                                placeholder='Введите почту'
                                type='email'
                                autoComplete='email'
                                aria-label='Email'
                                {...register('email', {
                                    required: 'Почта обязательна',
                                    pattern: {
                                        value: EMAIL_PATTERN,
                                        message: 'Введите корректный email',
                                    },
                                })}
                            />
                        ) : (
                            <Input
                                className='w-full'
                                size='lg'
                                placeholder='Введите почту'
                                value={me?.email ?? ''}
                                type='email'
                                disabled
                                aria-label='Email'
                            />
                        )}
                    </Fieldset>
                    {showResendVerification && (
                        <div className='flex justify-end mt-2'>
                            <Button
                                type='button'
                                emphasis='secondary'
                                variant='ghost'
                                size='md'
                                loading={isResendPending}
                                disabled={isResendPending || meLoading}
                                onClick={handleResendClick}
                                onKeyDown={handleResendKeyDown}
                                tabIndex={0}
                                aria-label='Отправить письмо с подтверждением почты ещё раз'
                            >
                                Отправить письмо ещё раз
                            </Button>
                        </div>
                    )}
                </div>

                {showPasswordSection && (
                    <>
                        <div className='grid-col-1'>
                            <Fieldset label='Пароль'>
                                <InputPassword
                                    className='w-full'
                                    size='lg'
                                    placeholder='***************'
                                    name='password'
                                    disabled
                                />
                            </Fieldset>
                        </div>
                        {isError && <p className='text-sm text-red-500'>{form.formState.errors.root?.message}</p>}
                        <div className='grid-col-1'>
                            <div className='flex justify-end'>
                                <Button
                                    as='label'
                                    htmlFor='changePasswordModal'
                                    emphasis='secondary'
                                    variant='ghost'
                                    size='md'
                                >
                                    Изменить пароль
                                </Button>
                            </div>
                        </div>
                    </>
                )}

                {!showPasswordSection && isError && (
                    <p className='text-sm text-red-500'>{form.formState.errors.root?.message}</p>
                )}

                <div className='grid-col-1 actions'>
                    <Button
                        color='accent'
                        size='lg'
                        type='submit'
                        disabled={meLoading || isPending}
                        loading={isPending}
                    >
                        Сохранить
                    </Button>
                </div>
            </form>
            {showPasswordSection && (
                <Modal id='changePasswordModal'>
                    <h3 className='text-lg font-bold'>Смена пароля</h3>
                    <MeChangePasswordForm />
                </Modal>
            )}
        </>
    )
}
